/*
  Hat Trick OS (sections/hat-trick-os.liquid): windows that open, drag,
  resize, stack and minimize into the Dock; the menu bar; the Finder-style
  window and product windows that add to the cart in place; Preferences;
  and the apps. Everything that sells is a plain link without it.

  Motion only ever answers a click or a pointer: minimizing scales into the
  Dock, a launched app hops once, the Dock magnifies under the mouse. All
  of it is skipped with reduced motion.
*/
(() => {
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const PREFS_KEY = 'os-prefs';
  const DEFAULTS = {
    appearance: 'blue',
    highlight: 'blue',
    desktop: 'aqua',
    dockSize: 'medium',
    dockMag: true,
    dockHide: false,
    minimize: 'scale',
    bounce: true,
    view: 'icon',
    toolbar: true,
    capper: true,
    pigeon: true,
  };
  const phone = window.matchMedia('(max-width: 699px)');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const store = {
    get(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {}
    },
  };

  function el(tag, props = {}, ...kids) {
    const node = Object.assign(document.createElement(tag), props);
    node.append(...kids.filter((k) => k != null));
    return node;
  }

  function init(os) {
    if (!os || os.dataset.ready) return;
    os.dataset.ready = '1';

    const html = document.documentElement;
    const desk = os.querySelector('[data-desk]');
    const dock = os.querySelector('[data-dock]');
    const dockList = os.querySelector('[data-dock-list]');
    const dockEnd = dockList.querySelector('[data-dock-end]');
    let z = 10;

    /* Preferences */

    let prefs = { ...DEFAULTS };
    try {
      prefs = { ...DEFAULTS, ...JSON.parse(store.get(PREFS_KEY) || '{}') };
    } catch (e) {}

    function applyPrefs() {
      html.dataset.appearance = prefs.appearance;
      html.dataset.highlight = prefs.highlight;
      html.dataset.desktop = prefs.desktop;
      html.dataset.dockSize = prefs.dockSize;
      html.dataset.dockHide = String(Boolean(prefs.dockHide));
      const cap = os.querySelector('[data-capper]');
      if (cap && !prefs.capper) cap.hidden = true;
      if (typeof pigeonApi !== 'undefined') pigeonApi.sync();
      os.querySelectorAll('[data-pref]').forEach((input) => {
        const value = prefs[input.dataset.pref];
        if (input.type === 'checkbox') input.checked = Boolean(value);
        else if (input.type === 'radio') input.checked = input.value === value;
        else input.value = value;
      });
      labelCommands();
    }

    function setPref(key, value) {
      prefs[key] = value;
      store.set(PREFS_KEY, JSON.stringify(prefs));
      applyPrefs();
    }

    os.addEventListener('change', (e) => {
      const input = e.target.closest('[data-pref]');
      if (!input) return;
      if (input.type === 'radio' && !input.checked) return;
      setPref(input.dataset.pref, input.type === 'checkbox' ? input.checked : input.value);
    });

    const tabs = [...os.querySelectorAll('[data-tab]')];
    function prefsPane(name) {
      tabs.forEach((t) => {
        const on = t.dataset.tab === name;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        os.querySelector(`[data-pane-body="${t.dataset.tab}"]`).hidden = !on;
      });
    }
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => prefsPane(t.dataset.tab));
      t.addEventListener('keydown', (e) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!step) return;
        const next = tabs[(i + step + tabs.length) % tabs.length];
        prefsPane(next.dataset.tab);
        next.focus();
      });
    });

    /* Windows */

    const win = (id) => os.querySelector(`.os-win[data-win="${CSS.escape(id)}"]`);
    const titleOf = (w) => w.querySelector('[data-win-title]').textContent.trim();
    const shown = () => [...os.querySelectorAll('.os-win:not([hidden])')];

    function place(w) {
      if (w.dataset.placed || !desk.clientWidth) return;
      w.dataset.placed = '1';
      const dw = desk.clientWidth;
      const dh = desk.clientHeight;
      const x = (dw * Number(w.dataset.x)) / 100;
      const y = (dh * Number(w.dataset.y)) / 100;
      w.style.left = `${clamp(x, 8, Math.max(8, dw - w.offsetWidth - 8))}px`;
      w.style.top = `${clamp(y, 8, Math.max(8, dh - w.offsetHeight - 70))}px`;
    }

    function focus(w) {
      w.style.zIndex = ++z;
      os.querySelectorAll('.os-win.is-active').forEach((o) => o !== w && o.classList.remove('is-active'));
      w.classList.add('is-active');
      sync();
    }

    function focusTop() {
      const open = shown().sort((a, b) => b.style.zIndex - a.style.zIndex);
      os.querySelectorAll('.os-win.is-active').forEach((o) => o.classList.remove('is-active'));
      if (open[0]) focus(open[0]);
      else sync();
    }

    const moving = () => !still.matches && typeof Element.prototype.animate === 'function';

    // A launched app's Dock icon hops three times, each lower, like 2001.
    function bounce(item) {
      if (!item || !prefs.bounce || !moving() || item.dataset.bouncing) return;
      const h = item.offsetHeight || 52;
      const up = 'cubic-bezier(.2, .7, .4, 1)';
      const down = 'cubic-bezier(.6, 0, .8, .4)';
      item.dataset.bouncing = '1';
      item
        .animate(
          [
            { transform: 'translateY(0)', easing: up },
            { transform: `translateY(${-h * 0.5}px)`, offset: 0.22, easing: down },
            { transform: 'translateY(0)', offset: 0.44, easing: up },
            { transform: `translateY(${-h * 0.28}px)`, offset: 0.62, easing: down },
            { transform: 'translateY(0)', offset: 0.78, easing: up },
            { transform: `translateY(${-h * 0.1}px)`, offset: 0.9, easing: down },
            { transform: 'translateY(0)' },
          ],
          { duration: 1000 }
        )
        .finished.catch(() => {})
        .finally(() => delete item.dataset.bouncing);
    }
    const dockItemFor = (w) =>
      w.dataset.app && w.dataset.app !== 'finder' ? dockList.querySelector(`[data-dock-app="${CSS.escape(w.dataset.app)}"] > :is(button, a)`) : null;

    // Scale Effect: the window shrinks into its Dock tile, or grows back out.
    function scaleEffect(w, tile, reverse) {
      if (prefs.minimize === 'none' || !moving() || !tile) return Promise.resolve();
      const a = w.getBoundingClientRect();
      const b = tile.getBoundingClientRect();
      const dx = b.left + b.width / 2 - (a.left + a.width / 2);
      const dy = b.top + b.height / 2 - (a.top + a.height / 2);
      const s = Math.max(b.width / a.width, 0.04);
      const frames = [
        { transform: 'none', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 0.35 },
      ];
      if (reverse) frames.reverse();
      return w.animate(frames, { duration: 300, easing: reverse ? 'ease-out' : 'ease-in' }).finished.catch(() => {});
    }

    function tileFor(w) {
      return dockList.querySelector(`[data-dock-min="${CSS.escape(w.dataset.win)}"]`);
    }

    function addTile(w) {
      let tile = tileFor(w);
      if (tile) return tile;
      const mini = el('span', { className: 'os-dock__mini' });
      mini.innerHTML = w.querySelector('.os-win__proxy').innerHTML;
      tile = el(
        'li',
        { className: 'os-dock__item os-dock__min' },
        el('button', { type: 'button' }, mini, el('span', { className: 'os-dock__label', textContent: titleOf(w) }))
      );
      tile.dataset.dockMin = w.dataset.win;
      tile.firstChild.dataset.restore = w.dataset.win;
      tile.firstChild.setAttribute('aria-label', `Restore ${titleOf(w)}`);
      dockList.insertBefore(tile, dockEnd);
      return tile;
    }

    function open(id, { user = false, from = null } = {}) {
      const w = win(id);
      if (!w) return;
      if (w.classList.contains('is-min')) return restore(w);
      const launching = w.hidden;
      w.hidden = false;
      place(w);
      focus(w);
      if (user) w.focus({ preventScroll: true });
      // Opening an app from anywhere (Dock, desktop, Applications, a menu) hops its Dock icon.
      if (launching && (user || from)) bounce(dockItemFor(w) || (from && from.closest('[data-dock]') ? from : null));
      w.dispatchEvent(new CustomEvent('os:open'));
      os.dispatchEvent(new CustomEvent('os:opened', { detail: { id, user } }));
    }

    async function restore(w) {
      const tile = tileFor(w);
      w.hidden = false;
      w.classList.remove('is-min');
      focus(w);
      w.focus({ preventScroll: true });
      await scaleEffect(w, tile, true);
      if (tile) tile.remove();
      sync();
      w.dispatchEvent(new CustomEvent('os:open'));
    }

    async function minimize(w) {
      if (!w || w.hidden || w.dataset.busy) return;
      w.dataset.busy = '1';
      const tile = addTile(w);
      w.classList.remove('is-active');
      await scaleEffect(w, tile, false);
      delete w.dataset.busy;
      w.hidden = true;
      w.classList.add('is-min');
      focusTop();
      w.dispatchEvent(new CustomEvent('os:close', { detail: { minimized: true } }));
    }

    function close(w) {
      if (!w) return;
      w.hidden = true;
      w.classList.remove('is-min', 'is-active', 'is-max');
      const tile = tileFor(w);
      if (tile) tile.remove();
      focusTop();
      w.dispatchEvent(new CustomEvent('os:close'));
    }

    function toggleToolbar(w) {
      const bar = w && w.querySelector('[data-toolbar]');
      if (!bar) return;
      bar.hidden = !bar.hidden;
      const pill = w.querySelector('[data-act="toolbar"]');
      pill.setAttribute('aria-pressed', String(!bar.hidden));
      pill.setAttribute('aria-label', bar.hidden ? 'Show toolbar' : 'Hide toolbar');
      if (w.dataset.win === 'finder') setPref('toolbar', !bar.hidden);
    }

    const active = () => os.querySelector('.os-win.is-active:not([hidden])');
    const windowList = os.querySelector('[data-window-list]');

    // Running lights in the Dock and the Window menu follow the open windows.
    function sync() {
      const all = [...os.querySelectorAll('.os-win')].filter((w) => !w.hidden || w.classList.contains('is-min'));
      const apps = new Set(all.map((w) => w.dataset.app).filter(Boolean));
      dockList.querySelectorAll('[data-dock-app]').forEach((item) => item.classList.toggle('is-running', apps.has(item.dataset.dockApp)));
      windowList.replaceChildren(
        ...all.map((w) => {
          const b = el('button', { type: 'button', textContent: titleOf(w) });
          b.setAttribute('role', 'menuitem');
          b.dataset.restore = w.dataset.win;
          if (w.classList.contains('is-active')) b.classList.add('is-front');
          return el('li', { role: 'none' }, b);
        })
      );
      labelCommands();
    }

    os.addEventListener('pointerdown', (e) => {
      const w = e.target.closest('.os-win');
      if (w && !w.classList.contains('is-active')) focus(w);
    });

    os.addEventListener('click', (e) => {
      const act = e.target.closest('.os-win__bar [data-act]');
      if (!act) return;
      const w = act.closest('.os-win');
      if (act.dataset.act === 'close') close(w);
      if (act.dataset.act === 'min') minimize(w);
      if (act.dataset.act === 'zoom' && !phone.matches) w.classList.toggle('is-max');
      if (act.dataset.act === 'toolbar') toggleToolbar(w);
    });

    // Double-clicking a title bar minimizes, as it did in 2001.
    os.addEventListener('dblclick', (e) => {
      const bar = e.target.closest('.os-win__bar');
      if (bar && !e.target.closest('button') && !phone.matches) minimize(bar.closest('.os-win'));
    });

    function track(handle, e, onMove) {
      handle.setPointerCapture(e.pointerId);
      const up = () => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', up);
        handle.removeEventListener('pointercancel', up);
        handle.closest('.os-win').classList.remove('is-dragging', 'is-resizing');
      };
      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', up);
      handle.addEventListener('pointercancel', up);
    }

    os.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || phone.matches) return;
      const bar = e.target.closest('.os-win__bar');
      const grip = e.target.closest('[data-grip]');
      if (bar && !e.target.closest('button')) {
        const w = bar.closest('.os-win');
        if (w.classList.contains('is-max')) return;
        const dx = e.clientX - w.offsetLeft;
        const dy = e.clientY - w.offsetTop;
        w.classList.add('is-dragging');
        track(bar, e, (ev) => {
          w.style.left = `${clamp(ev.clientX - dx, 80 - w.offsetWidth, desk.clientWidth - 80)}px`;
          w.style.top = `${clamp(ev.clientY - dy, 0, desk.clientHeight - 30)}px`;
        });
      } else if (grip) {
        const w = grip.closest('.os-win');
        const sx = e.clientX;
        const sy = e.clientY;
        const sw = w.offsetWidth;
        const sh = w.offsetHeight;
        w.classList.add('is-resizing');
        track(grip, e, (ev) => {
          w.style.width = `${clamp(sw + ev.clientX - sx, 260, desk.clientWidth - w.offsetLeft - 4)}px`;
          w.style.height = `${clamp(sh + ev.clientY - sy, 160, desk.clientHeight - w.offsetTop - 4)}px`;
        });
        e.preventDefault();
      }
    });

    window.addEventListener('resize', () => {
      os.querySelectorAll('.os-win[data-placed]').forEach((w) => {
        w.style.left = `${clamp(w.offsetLeft, 80 - w.offsetWidth, desk.clientWidth - 80)}px`;
        w.style.top = `${clamp(w.offsetTop, 0, desk.clientHeight - 30)}px`;
      });
    });

    /* Menu bar */

    const menus = [...os.querySelectorAll('.os-menu')];
    let openMenu = null;
    const titleBtn = (m) => m.querySelector('.os-menu__title');
    const listOf = (m) => m.querySelector(':scope > .os-menu__list');
    const itemsIn = (list) => [...list.querySelectorAll(':scope > li > :is(button, a)')].filter((i) => i.offsetParent !== null);
    const visibleMenus = () => menus.filter((m) => m.offsetParent !== null);

    function showMenu(m, focusFirst = false) {
      if (openMenu && openMenu !== m) hideMenu();
      openMenu = m;
      titleBtn(m).setAttribute('aria-expanded', 'true');
      listOf(m).hidden = false;
      labelCommands();
      if (focusFirst) itemsIn(listOf(m))[0]?.focus();
    }

    function hideMenu(refocus = false) {
      if (!openMenu) return;
      const m = openMenu;
      openMenu = null;
      titleBtn(m).setAttribute('aria-expanded', 'false');
      listOf(m).hidden = true;
      if (refocus) titleBtn(m).focus();
    }

    menus.forEach((m) => {
      const t = titleBtn(m);
      t.addEventListener('click', () => (openMenu === m ? hideMenu() : showMenu(m)));
      t.addEventListener('pointerenter', () => openMenu && openMenu !== m && showMenu(m));
    });

    os.querySelector('[data-menus]').addEventListener('keydown', (e) => {
      const m = e.target.closest('.os-menu');
      if (!m) return;
      const onTitle = e.target === titleBtn(m);
      const ms = visibleMenus();
      const side = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      const sub = e.target.closest('.os-menu__sub > .os-menu__list');
      if (e.key === 'Escape') {
        hideMenu(true);
        e.preventDefault();
      } else if (onTitle && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        showMenu(m, true);
        e.preventDefault();
      } else if (side === 1 && e.target.matches('.os-menu__sub > button')) {
        itemsIn(e.target.parentElement.querySelector('.os-menu__list'))[0]?.focus();
        e.preventDefault();
      } else if (side === -1 && sub) {
        sub.parentElement.querySelector(':scope > button').focus();
        e.preventDefault();
      } else if (side) {
        const next = ms[(ms.indexOf(m) + side + ms.length) % ms.length];
        if (openMenu) showMenu(next, true);
        else titleBtn(next).focus();
        e.preventDefault();
      } else if (!onTitle && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        const list = e.target.closest('.os-menu__list');
        const items = itemsIn(list);
        const i = items.indexOf(e.target);
        items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
        e.preventDefault();
      }
    });

    document.addEventListener('pointerdown', (e) => {
      if (openMenu && !e.target.closest('[data-menus]')) hideMenu();
      if (html.dataset.dockHide === 'true') dock.classList.toggle('is-peek', e.clientY > window.innerHeight - 12 || Boolean(e.target.closest('[data-dock]')));
    });

    function labelCommands() {
      const finderWin = win('finder');
      const tb = finderWin && finderWin.querySelector('[data-toolbar]');
      os.querySelectorAll('[data-cmd-label]').forEach((b) => {
        if (b.dataset.cmd === 'dock-mag') b.textContent = `Turn Magnification ${prefs.dockMag ? 'Off' : 'On'}`;
        if (b.dataset.cmd === 'dock-hide') b.textContent = `Turn Hiding ${prefs.dockHide ? 'Off' : 'On'}`;
        if (b.dataset.cmd === 'toolbar') b.textContent = tb && tb.hidden ? 'Show Toolbar' : 'Hide Toolbar';
        if (b.dataset.cmd === 'capper') b.textContent = prefs.capper ? 'Hide Capper' : 'Show Capper';
        if (b.dataset.cmd === 'pigeon') b.textContent = prefs.pigeon ? 'Shoo the Pigeon' : 'Bring Back the Pigeon';
      });
      os.querySelectorAll('[data-cmd^="view-"]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.cmd === `view-${prefs.view}`)));
    }

    function command(cmd) {
      if (cmd === 'close') close(active());
      if (cmd === 'minimize') minimize(active());
      if (cmd === 'front') focusTop();
      if (cmd === 'dock-mag') setPref('dockMag', !prefs.dockMag);
      if (cmd === 'dock-hide') setPref('dockHide', !prefs.dockHide);
      if (cmd === 'capper') {
        setPref('capper', !prefs.capper);
        const cap = os.querySelector('[data-capper]');
        if (prefs.capper && cap) {
          cap.querySelector('[data-capper-balloon]').hidden = true;
          cap.querySelector('[data-capper-char]').click();
        }
      }
      if (cmd === 'pigeon') setPref('pigeon', !prefs.pigeon);
      if (cmd === 'view-icon' || cmd === 'view-list') {
        setView(cmd.slice(5));
        open('finder');
      }
      if (cmd === 'toolbar') {
        open('finder');
        toggleToolbar(win('finder'));
      }
    }

    /* Anything with data-open, data-go, data-cmd, data-product or data-restore */

    os.addEventListener('click', (e) => {
      const t = e.target.closest('[data-open], [data-go], [data-cmd], [data-product], [data-restore]');
      if (!t || t.closest('.os-win__bar [data-act]')) return;
      // Let modified clicks on real links open a new tab as usual.
      if (t.tagName === 'A' && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
      e.preventDefault();
      if (t.dataset.product) openProduct(t);
      else if (t.dataset.restore) {
        const w = win(t.dataset.restore);
        if (w) w.classList.contains('is-min') ? restore(w) : focus(w);
      } else if (t.dataset.cmd) command(t.dataset.cmd);
      else {
        if (t.dataset.go) go(t.dataset.go);
        const id = t.dataset.open || (t.dataset.go ? 'finder' : null);
        if (id) open(id, { user: true, from: t });
        if (t.dataset.pane) prefsPane(t.dataset.pane);
      }
      if (t.closest('[data-menus]')) hideMenu();
      os.querySelectorAll('.os-item__link.is-selected').forEach((i) => i.classList.remove('is-selected'));
      if (t.classList.contains('os-item__link')) t.classList.add('is-selected');
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || openMenu) return;
      const w = active();
      if (w && w.contains(document.activeElement)) close(w);
    });

    /* Dock magnification, only for a mouse */

    let centers = null;
    let dockItems = [];
    const magnifies = () => prefs.dockMag && !still.matches && !phone.matches;
    dockList.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse' || !magnifies()) return;
      dockItems = [...dockList.querySelectorAll('.os-dock__item')];
      centers = dockItems.map((i) => {
        const r = i.getBoundingClientRect();
        return r.left + r.width / 2;
      });
      os.classList.add('is-magnifying');
    });
    dockList.addEventListener('pointermove', (e) => {
      if (!centers) return;
      // A tight falloff: the icon under the mouse grows most and its neighbors only a little, so they barely overlap.
      const reach = (parseFloat(getComputedStyle(html).getPropertyValue('--dock-size')) || 52) * 1.5;
      dockItems.forEach((item, k) => {
        const d = Math.min(Math.abs(e.clientX - centers[k]) / reach, 1);
        item.style.setProperty('--m', (1 + 0.5 * Math.cos((d * Math.PI) / 2)).toFixed(3));
        item.style.zIndex = d < 0.5 ? '3' : '';
      });
    });
    dockList.addEventListener('pointerleave', () => {
      centers = null;
      dockItems.forEach((item) => {
        item.style.removeProperty('--m');
        item.style.zIndex = '';
      });
      setTimeout(() => centers || os.classList.remove('is-magnifying'), 120);
    });

    /* Finder */

    const finder = os.querySelector('[data-finder]');
    const finderWin = win('finder');
    const folders = [...finder.querySelectorAll('[data-folder]')];
    let here = finder.dataset.start;
    const trail = [];

    function showFolder(id) {
      const folder = finder.querySelector(`[data-folder="${CSS.escape(id)}"]`);
      if (!folder) return false;
      folders.forEach((f) => (f.hidden = f !== folder));
      here = id;
      finderWin.querySelector('[data-win-title]').textContent = folder.dataset.title;
      const count = folder.querySelectorAll('.os-item').length;
      finderWin.querySelector('[data-win-status]').textContent = folder.dataset.count || `${count} item${count === 1 ? '' : 's'}`;
      finderWin.querySelector('[data-go="back"]').disabled = trail.length === 0;
      finderWin.querySelectorAll('.os-tb [data-go]').forEach((b) => b.dataset.go !== 'back' && b.setAttribute('aria-current', String(b.dataset.go === id)));
      finderWin.querySelector('.os-win__body').scrollTop = 0;
      sync();
      return true;
    }

    function go(id) {
      if (id === 'back') {
        const prev = trail.pop();
        if (prev) showFolder(prev);
        return;
      }
      if (id === here) return;
      const from = here;
      if (showFolder(id)) trail.push(from);
      finderWin.querySelector('[data-go="back"]').disabled = trail.length === 0;
    }

    function setView(view) {
      if (view !== 'icon' && view !== 'list') return;
      finder.dataset.view = view;
      finderWin.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.view === view)));
      if (prefs.view !== view) setPref('view', view);
      else labelCommands();
    }
    finderWin.querySelectorAll('[data-view]').forEach((b) => b.addEventListener('click', () => setView(b.dataset.view)));

    /* Product windows, cloned from the template and filled from /products/{handle}.js */

    const tpl = os.querySelector('[data-product-tpl]');
    const productData = new Map();
    let productCount = 0;
    const currency = () => (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const money = (cents, cur = currency()) =>
      new Intl.NumberFormat(html.lang || 'en-US', { style: 'currency', currency: cur }).format(cents / 100);
    const sized = (src, width) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${width}` : '');

    function fetchProduct(handle) {
      if (!productData.has(handle)) {
        productData.set(
          handle,
          fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`).then((res) => {
            if (!res.ok) throw new Error(res.status);
            return res.json();
          })
        );
      }
      return productData.get(handle).catch((err) => {
        productData.delete(handle);
        throw err;
      });
    }

    const propsTemplate = (link) => link.parentElement.querySelector('template[data-props]');
    function propsOf(link) {
      const out = {};
      const t = propsTemplate(link);
      if (t) t.content.querySelectorAll('input[name^="properties["]').forEach((i) => (out[i.name.slice(11, -1)] = i.value));
      return out;
    }

    function openProduct(link) {
      const handle = link.dataset.product;
      const id = `p-${handle}`;
      if (win(id)) return open(id, { user: true });
      const w = tpl.content.firstElementChild.cloneNode(true);
      const name = link.querySelector('.os-item__name').textContent.trim();
      w.dataset.win = id;
      w.setAttribute('aria-labelledby', `os-t-${id}`);
      w.querySelector('.os-win__title').id = `os-t-${id}`;
      w.querySelector('[data-win-title]').textContent = name;
      w.querySelectorAll('.os-light').forEach((b) => b.setAttribute('aria-label', b.getAttribute('aria-label').replace('Product', name)));
      const n = productCount++ % 6;
      w.dataset.x = String(14 + n * 3);
      w.dataset.y = String(5 + n * 3);
      const art = w.querySelector('.os-product');
      art.classList.add('is-loading');
      const img = w.querySelector('[data-p-img]');
      const thumb = link.querySelector('img');
      if (thumb) img.src = thumb.currentSrc || thumb.src;
      w.querySelector('[data-p-url]').href = link.href;
      desk.append(w);
      open(id, { user: true });
      fetchProduct(handle)
        .then((p) => fillProduct(w, p, link))
        .catch(() => {
          art.classList.remove('is-loading');
          w.querySelector('[data-p-title]').textContent = name;
          w.querySelector('[data-add]').hidden = true;
          w.querySelector('[data-p-price]').textContent = 'This one didn’t load. Open the full product page instead.';
        });
    }

    function fillProduct(w, p, link) {
      const $ = (sel) => w.querySelector(sel);
      $('.os-product').classList.remove('is-loading');
      $('[data-p-title]').textContent = p.title;
      $('[data-win-title]').textContent = p.title;

      const images = (p.images || []).filter(Boolean);
      const alts = (p.media || []).map((m) => m.alt);
      const stage = $('[data-p-img]');
      const show = (i) => {
        stage.src = sized(images[i], 800);
        stage.alt = alts[i] || p.title;
        $('[data-p-thumbs]').querySelectorAll('button').forEach((b, k) => b.setAttribute('aria-current', String(k === i)));
      };
      if (images.length) show(0);
      if (images.length > 1) {
        $('[data-p-thumbs]').replaceChildren(
          ...images.slice(0, 8).map((src, i) =>
            el(
              'li',
              {},
              el(
                'button',
                { type: 'button', ariaLabel: `Photo ${i + 1}`, onclick: () => show(i) },
                el('img', { src: sized(src, 120), alt: '', width: 40, height: 40, loading: 'lazy' })
              )
            )
          )
        );
        show(0);
      }

      const preorder = link.hasAttribute('data-preorder');
      const closesAt = Number(link.dataset.closes || 0) * 1000;
      const closed = preorder && closesAt && Date.now() > closesAt;
      if (preorder) {
        $('[data-p-facts]').hidden = false;
        $('[data-p-closes]').textContent = closesAt
          ? `${closed ? 'Closed' : 'Closes'} ${new Date(closesAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`
          : 'Open now';
        const ships = propsOf(link)['Ships by'];
        if (ships) $('[data-p-ships]').textContent = ships;
        else $('[data-p-ships-row]').hidden = true;
        const t = propsTemplate(link);
        if (t) $('[data-p-props]').replaceChildren(t.content.cloneNode(true));
      }

      const select = $('[data-p-variants]');
      const variants = p.variants || [];
      select.replaceChildren(
        ...variants.map((v) => {
          const o = el('option', { value: v.id, textContent: v.available ? v.title : `${v.title} (sold out)` });
          o.disabled = !v.available;
          return o;
        })
      );
      const first = variants.find((v) => v.available) || variants[0];
      if (first) select.value = first.id;
      $('[data-p-size]').hidden = variants.length < 2;
      if (p.options && p.options[0] && p.options[0].name && p.options[0].name !== 'Title') {
        $('[data-p-size] .os-field__label').textContent = `${p.options[0].name}:`;
      }

      const buy = $('[data-p-buy]');
      const update = () => {
        const v = variants.find((x) => String(x.id) === select.value) || first;
        $('[data-p-price]').textContent = v ? money(v.price) : '';
        const ok = v && v.available && !closed;
        buy.disabled = !ok;
        buy.textContent = closed ? 'Pre-order Closed' : !v || !v.available ? 'Sold Out' : preorder ? 'Pre-order' : 'Add to Cart';
      };
      select.addEventListener('change', update);
      update();

      $('[data-p-desc]').innerHTML = p.description || '';
    }

    /* Cart */

    async function cartCall(path, body) {
      const res = await fetch(`${ROOT}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: body instanceof FormData ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body instanceof FormData ? body : body && JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Try again.');
      return data;
    }

    function renderCart(cart) {
      os.querySelectorAll('[data-cart-count]').forEach((n) => (n.textContent = cart.item_count));
      const badge = os.querySelector('[data-cart-badge]');
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
      os.querySelector('[data-cart-total]').textContent = money(cart.total_price, cart.currency);
      os.querySelector('[data-cart-discount-row]').hidden = !cart.total_discount;
      os.querySelector('[data-cart-discount]').textContent = `−${money(cart.total_discount || 0, cart.currency)}`;
      os.querySelector('[data-cart-empty]').hidden = cart.item_count > 0;
      os.querySelector('[data-checkout]').disabled = cart.item_count === 0;

      os.querySelector('[data-cart-lines]').replaceChildren(
        ...cart.items.map((item) => {
          const img = item.image ? el('img', { src: sized(item.image, 96), alt: '', width: 48, height: 48 }) : el('span');
          const ships = item.properties && item.properties['Ships by'];
          const variant = item.variant_title && !/default title/i.test(item.variant_title) ? item.variant_title : '';
          const qty = (n, label, text) =>
            el('button', { type: 'button', className: 'os-btn os-btn--small', textContent: text, ariaLabel: label, onclick: () => change(item.key, n) });
          return el(
            'li',
            { className: 'os-line' },
            img,
            el(
              'div',
              {},
              el('p', { className: 'os-line__title', textContent: item.product_title }),
              variant || ships ? el('p', { className: 'os-line__meta', textContent: [variant, ships].filter(Boolean).join(' · ') }) : null,
              el(
                'div',
                { className: 'os-qty' },
                qty(item.quantity - 1, `One fewer ${item.product_title}`, '−'),
                el('span', { textContent: item.quantity }),
                qty(item.quantity + 1, `One more ${item.product_title}`, '+'),
                el('button', { type: 'button', className: 'os-textbtn', textContent: 'Remove', onclick: () => change(item.key, 0) })
              )
            ),
            el('span', { className: 'os-line__price', textContent: money(item.original_line_price, cart.currency) })
          );
        })
      );
    }

    async function change(key, quantity) {
      try {
        renderCart(await cartCall('cart/change.js', { id: key, quantity }));
      } catch (err) {
        window.alert(err.message);
      }
    }

    os.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[data-add]');
      if (!form) return;
      e.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const status = form.querySelector('[data-status]');
      const label = button.textContent;
      button.disabled = true;
      status.textContent = 'Adding…';
      try {
        await cartCall('cart/add.js', new FormData(form));
        renderCart(await cartCall('cart.js'));
        status.textContent = 'Added to your cart.';
        open('cart');
        os.dispatchEvent(new CustomEvent('os:added'));
      } catch (err) {
        status.textContent = err.message;
      } finally {
        button.disabled = false;
        button.textContent = label;
      }
    });

    // "Add all to cart" on a folder with a set offer: adds each pre-order in
    // the folder that isn't in the cart yet, with its pre-order properties.
    os.addEventListener('click', async (e) => {
      const button = e.target.closest('[data-add-set]');
      if (!button) return;
      const box = button.closest('[data-set]');
      const status = box.querySelector('[data-set-status]');
      const links = [...button.closest('[data-folder]').querySelectorAll('.os-item__link[data-preorder]')];
      button.disabled = true;
      status.textContent = 'Adding…';
      try {
        const cart = await cartCall('cart.js');
        const have = new Set(cart.items.map((i) => String(i.variant_id)));
        const items = links.filter((l) => !have.has(l.dataset.variant)).map((l) => ({ id: Number(l.dataset.variant), quantity: 1, properties: propsOf(l) }));
        if (items.length) await cartCall('cart/add.js', { items });
        renderCart(await cartCall('cart.js'));
        status.textContent = items.length
          ? `Added ${items.length} hat${items.length === 1 ? '' : 's'}. The free one comes off at checkout.`
          : 'They’re all in your cart already.';
        open('cart');
      } catch (err) {
        status.textContent = err.message;
      } finally {
        button.disabled = false;
      }
    });

    cartCall('cart.js').then(renderCart).catch(() => {});

    /* Clock */

    const clock = os.querySelector('[data-clock]');
    function tick() {
      const now = new Date();
      clock.dateTime = now.toISOString();
      clock.textContent = `${now.toLocaleDateString([], { weekday: 'short' })} ${now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    }
    tick();
    setInterval(tick, 15000);

    /*
      Capper, the hat assistant. He only speaks up in answer to something
      you did, each tip once per visit, and stays gone once dismissed for
      good. The pigeon's antics can set him off too.
    */
    const capper = os.querySelector('[data-capper]');
    const say = (() => {
      if (!capper) return () => {};
      const balloon = capper.querySelector('[data-capper-balloon]');
      const msg = capper.querySelector('[data-capper-msg]');
      const opts = capper.querySelector('[data-capper-opts]');
      const never = capper.querySelector('[data-capper-never]');
      const pupils = [...capper.querySelectorAll('[data-pupil]')];
      const SEEN = 'os-capper-seen';
      let seen = [];
      try {
        seen = JSON.parse(sessionStorage.getItem(SEEN) || '[]');
      } catch (e) {}
      const setFolder = os.querySelector('[data-set]')?.closest('[data-folder]')?.dataset.folder;
      const bye = () => {
        balloon.hidden = true;
      };
      const JOKES = [
        'What do you call a hat that won’t stop talking? Me. Hi.',
        'I’m one size fits most. Most people, not most heads.',
        'The pigeon is not with me. I want that on the record.',
        'Tip: double-click a title bar to tuck a window into the Dock.',
        'Tip: drag the bottom-right corner of a window to make it bigger.',
        'Tip: Preferences has a Graphite look, if all this blue is too much.',
        'I’ve been on the same head since 1994. Well, I’ve been on a lot of heads.',
        'Tip: the Trash isn’t really the Trash. Open it.',
      ];
      let joke = Math.floor(Math.random() * JOKES.length);
      const TIPS = {
        product: {
          text: 'It looks like you’re trying to buy a hat! Would you like help?',
          opts: [
            ['Which size fits me?', () => show({ text: 'Our snapbacks adjust to fit most heads, about 19 to 24 inches. Vintage pieces are one of a kind, so check the size in the name.', opts: [['Got it', bye]] })],
            ['Is there a deal?', () =>
              show({
                text: 'Buy 3 Five Borough hats and the 4th is free. Want to see them?',
                opts: [
                  ['Show me', () => {
                    bye();
                    if (setFolder) go(setFolder);
                    open('finder', { user: true });
                  }],
                  ['Maybe later', bye],
                ],
              })],
            ['Just browsing', bye],
          ],
        },
        added: {
          text: 'Nice pick! Want to check out, or keep shopping?',
          opts: [
            ['Check out', () => (location.href = `${ROOT}checkout`)],
            ['Keep shopping', bye],
          ],
        },
        ballot: { text: 'Relax, nothing in this Trash is really gone. It’s the Comeback Ballot: vote a retired hat back into production.', opts: [['Got it', bye]] },
        solitaire: { text: 'It looks like you’re avoiding work. I fully support this. Win and there’s a little something in it for you.', opts: [['Deal me in', bye]] },
        radio: { text: 'Tip: Hot 97, Power 105.1 and WFAN all play right here. Drag the dial between stations for that sweet static.', opts: [['Nice', bye]] },
        mail: { text: 'Writing to us? A real human reads every message. Replies come by email.', opts: [['Good to know', bye]] },
        messenger: { text: 'Heads up: HatTrickNYC answers by email, not live. It’s 2001, be patient.', opts: [['LOL ok', bye]] },
        scores: { text: 'Tip: live games jump to the top of the board.', opts: [['Got it', bye]] },
        prefs: { text: 'Try the Skyline desktop picture. Trust me.', opts: [['Will do', bye]] },
        browser: { text: 'It looks like you’re trying to time travel. Every page comes from the Internet Archive, so give the past a second to load.', opts: [['Take me back', bye]] },
        pigeon: { text: 'Please stop poking the pigeon. He’s in a union.', opts: [['Sorry', bye], ['Shoo him away', () => {
          bye();
          setPref('pigeon', false);
        }]] },
      };

      function show(tip) {
        msg.textContent = tip.text;
        opts.replaceChildren(
          ...tip.opts.map(([label, fn]) => {
            const b = el('button', { type: 'button', textContent: label });
            b.addEventListener('click', fn);
            return el('li', {}, b);
          })
        );
        never.checked = false;
        const wasHidden = capper.hidden;
        capper.hidden = false;
        balloon.hidden = false;
        if (wasHidden) {
          capper.classList.remove('is-in');
          void capper.offsetWidth;
          capper.classList.add('is-in');
        }
      }

      capper.querySelector('[data-capper-close]').addEventListener('click', bye);
      never.addEventListener('change', () => {
        if (!never.checked) return;
        setPref('capper', false);
      });
      capper.querySelector('[data-capper-char]').addEventListener('click', () => {
        if (!balloon.hidden) return bye();
        const next = () => {
          joke = (joke + 1) % JOKES.length;
          show({ text: JOKES[joke], opts: [['Tell me another', next], ['Hide Capper', () => setPref('capper', false)]] });
        };
        next();
      });

      // His eyes follow the pointer (not with reduced motion).
      let pending = false;
      document.addEventListener('pointermove', (e) => {
        if (capper.hidden || still.matches || pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          pupils.forEach((p) => {
            const r = p.getBoundingClientRect();
            const a = Math.atan2(e.clientY - (r.top + r.height / 2), e.clientX - (r.left + r.width / 2));
            p.setAttribute('transform', `translate(${(Math.cos(a) * 2.6).toFixed(2)} ${(Math.sin(a) * 2.6).toFixed(2)})`);
          });
        });
      });

      return (key) => {
        if (!prefs.capper || !TIPS[key] || seen.includes(key)) return;
        seen.push(key);
        try {
          sessionStorage.setItem(SEEN, JSON.stringify(seen));
        } catch (e) {}
        show(TIPS[key]);
      };
    })();

    os.addEventListener('os:opened', (e) => {
      if (!e.detail.user) return;
      const id = e.detail.id;
      say(id.startsWith('p-') ? 'product' : id);
    });
    os.addEventListener('os:added', () => say('added'));

    /*
      The pigeon: stands on the front window's title bar, or on the Dock when
      nothing's open, and rides along when the window moves. Poke it and it
      waddles and coos; poke it three times fast and it flies to another
      perch. Nothing moves otherwise, and with reduced motion it just hops.
    */
    const pigeon = os.querySelector('[data-pigeon]');
    const pigeonApi = (() => {
      if (!pigeon) return { sync() {} };
      const bubble = pigeon.querySelector('[data-pigeon-say]');
      const PW = 44;
      const PH = 40;
      const COOS = ['coo.', 'coo coo', 'cOoOo?', 'got bread?', 'this is my window now', 'hey, I’m walkin’ here', '…', 'coo (aggressively)'];
      let perch = null;
      let offset = 90;
      let facing = 1;
      let pokes = [];
      let total = 0;
      let busy = false;
      let bubbleTimer = 0;
      let audioCtx = null;

      const perches = () => {
        const wins = phone.matches ? [] : [...os.querySelectorAll('.os-win:not([hidden])')].sort((a, b) => b.style.zIndex - a.style.zIndex);
        return [...wins, 'dock'];
      };
      const rectOf = (p) => (p === 'dock' ? dockList.getBoundingClientRect() : p.getBoundingClientRect());
      const valid = (p) => p === 'dock' || (p && p.isConnected && !p.hidden);
      function spot(p = perch) {
        const r = rectOf(p);
        offset = clamp(offset, 6, Math.max(6, r.width - PW - (p === 'dock' ? 6 : 70)));
        return { x: Math.round(r.left + offset), y: Math.round(r.top - PH + (p === 'dock' ? 4 : 3)) };
      }
      const at = (pt) => `translate(${pt.x}px, ${pt.y}px)`;
      function put() {
        if (!perch || busy) return;
        pigeon.style.transform = at(spot());
      }
      const face = (f) => pigeon.style.setProperty('--face', (facing = f));

      function coo() {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtx ||= new Ctx();
        const t = audioCtx.currentTime;
        const o = audioCtx.createOscillator();
        const g = audioCtx.createGain();
        const lfo = audioCtx.createOscillator();
        const depth = audioCtx.createGain();
        o.frequency.setValueAtTime(330, t);
        o.frequency.exponentialRampToValueAtTime(240, t + 0.35);
        lfo.frequency.value = 18;
        depth.gain.value = 14;
        lfo.connect(depth).connect(o.frequency);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.12, t + 0.04);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
        o.connect(g).connect(audioCtx.destination);
        o.start(t);
        lfo.start(t);
        o.stop(t + 0.42);
        lfo.stop(t + 0.42);
      }

      function speak(text) {
        bubble.textContent = text;
        bubble.hidden = false;
        clearTimeout(bubbleTimer);
        bubbleTimer = setTimeout(() => (bubble.hidden = true), 1500);
      }

      function walk() {
        const r = rectOf(perch);
        let dir = Math.random() < 0.5 ? -1 : 1;
        const room = Math.max(6, r.width - PW - (perch === 'dock' ? 6 : 70));
        if (offset < 50) dir = 1;
        if (offset > room - 50) dir = -1;
        const from = spot();
        offset = clamp(offset + dir * (40 + Math.random() * 80), 6, room);
        face(dir);
        const to = spot();
        if (!moving()) {
          pigeon.style.transform = at(to);
          return;
        }
        busy = true;
        pigeon.classList.add('is-walking');
        const dur = Math.abs(to.x - from.x) * 7 + 150;
        pigeon.animate([{ transform: at(from) }, { transform: at(to) }], { duration: dur, easing: 'linear' }).finished.finally(() => {
          busy = false;
          pigeon.classList.remove('is-walking');
          put();
          if (Math.random() < 0.5) {
            pigeon.classList.add('is-pecking');
            setTimeout(() => pigeon.classList.remove('is-pecking'), 260);
          }
        });
      }

      function fly(target) {
        const from = pigeon.style.transform.includes('-200px') ? null : spot();
        perch = target;
        offset = 20 + Math.random() * Math.max(20, rectOf(target).width - 140);
        const to = spot();
        if (!from || !moving()) {
          pigeon.style.transform = at(to);
          return;
        }
        face(to.x >= from.x ? 1 : -1);
        busy = true;
        pigeon.classList.add('is-flying');
        const mid = { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 90 };
        pigeon
          .animate([{ transform: at(from) }, { transform: at(mid), offset: 0.5 }, { transform: at(to) }], { duration: 750, easing: 'ease-in-out' })
          .finished.finally(() => {
            busy = false;
            pigeon.classList.remove('is-flying');
            put();
          });
      }

      pigeon.addEventListener('click', () => {
        if (busy) return;
        const t = Date.now();
        pokes = pokes.filter((p) => t - p < 2500).concat(t);
        total++;
        coo();
        if (total === 6) say('pigeon');
        if (pokes.length >= 3) {
          pokes = [];
          speak('fine. FINE.');
          const others = perches().filter((p) => p !== perch);
          fly(others[Math.floor(Math.random() * others.length)] || 'dock');
          return;
        }
        speak(COOS[Math.floor(Math.random() * COOS.length)]);
        walk();
      });

      // Ride along with the perch; find a new one when it closes.
      let queued = false;
      function check() {
        queued = false;
        if (pigeon.hidden) return;
        if (!valid(perch) || (phone.matches && perch !== 'dock')) return fly(perches()[0]);
        // A window opened on top of its spot: hop up onto that one.
        const front = perches()[0];
        if (!busy && front !== perch && front !== 'dock') {
          const b = pigeon.getBoundingClientRect();
          const f = front.getBoundingClientRect();
          const covered = b.right > f.left && b.left < f.right && b.bottom > f.top && b.top < f.bottom;
          if (covered) return fly(front);
        }
        put();
      }
      const soon = () => {
        if (!queued) {
          queued = true;
          requestAnimationFrame(check);
        }
      };
      new MutationObserver(soon).observe(desk, { subtree: true, attributes: true, attributeFilter: ['style', 'hidden', 'class'] });
      new MutationObserver(soon).observe(dockList, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });
      window.addEventListener('resize', soon);

      return {
        sync() {
          pigeon.hidden = !prefs.pigeon;
          if (pigeon.hidden) return;
          if (!valid(perch)) {
            perch = perches()[0];
            offset = 90;
          }
          put();
        },
      };
    })();

    /* Start up */

    applyPrefs();
    prefsPane('appearance');
    setView(prefs.view);
    if (!prefs.toolbar) toggleToolbar(finderWin);
    showFolder(here);

    initBrowser(os);
    initTv(os);
    initScores(os);
    initRadio(os);
    initBallot(os, open);
    initSolitaire(os);
    initMail(os);
    initMessenger(os, open);

    // The Read Me opens behind the Finder on a first visit, on big screens.
    if (!phone.matches && !store.get('os-seen') && !/[#&]auth=/.test(location.hash)) {
      open('readme');
      store.set('os-seen', '1');
    }
    place(finderWin);
    focus(finderWin);
    pigeonApi.sync();
  }

  /*
    Portal 2001: loads Wayback Machine captures (the if_ mode, which drops
    the archive's own toolbar) into a sandboxed frame. The archive picks
    the capture nearest the chosen year. Back and Forward walk the pages
    loaded from here; links clicked inside a page navigate the frame itself.
  */
  function initBrowser(os) {
    const box = os.querySelector('[data-web]');
    if (!box) return;
    const frame = box.querySelector('[data-web-frame]');
    const home = box.querySelector('[data-web-home]');
    const loading = box.querySelector('[data-web-loading]');
    const slow = box.querySelector('[data-web-slow]');
    const form = box.querySelector('[data-web-form]');
    const input = form.elements.url;
    const yearSel = box.querySelector('[data-web-year]');
    const status = box.querySelector('[data-web-status]');
    const outs = [...box.querySelectorAll('[data-web-out]')];
    const bookmarks = [...box.querySelectorAll('[data-wb]')];
    const HOME = 'hattrick://home';
    let trail = [{ url: HOME }];
    let at = 0;
    let slowTimer = 0;

    const normalize = (raw) => {
      let url = raw.trim();
      if (!url || /^hattrick:/i.test(url)) return HOME;
      if (!/^https?:\/\//i.test(url)) url = `http://${url.replace(/^\/+/, '')}`;
      return url;
    };
    const stamp = (year, md) => `${year}${md || '1015'}`;
    // Bookmarks carry a checked capture per year (data-snaps), because the
    // archive's "nearest capture" can land on a redirect or a robots.txt.
    // Typed addresses ask the archive's availability API, with a plain
    // nearest-date request as the fallback.
    function snapFor(entry) {
      if (entry.snap) return entry.snap;
      if (entry.snaps) {
        const years = Object.keys(entry.snaps).filter((y) => entry.snaps[y]);
        if (years.length) {
          const best = years.reduce((a, b) => (Math.abs(b - entry.year) < Math.abs(a - entry.year) ? b : a));
          return entry.snaps[best];
        }
      }
      return `${stamp(entry.year, entry.md)}if_/${entry.url}`;
    }
    const archived = (entry, bare = false) => {
      const snap = snapFor(entry);
      return `https://web.archive.org/web/${bare ? snap.replace('if_/', '/') : snap}`;
    };
    async function lookUp(entry) {
      try {
        const res = await fetch(`https://archive.org/wayback/available?url=${encodeURIComponent(entry.url)}&timestamp=${stamp(entry.year)}`);
        const hit = (await res.json())?.archived_snapshots?.closest;
        if (hit && hit.available && /^\d{14}$/.test(hit.timestamp)) return { ...entry, snap: `${hit.timestamp}if_/${entry.url}` };
      } catch (e) {}
      return entry;
    }
    const fromMark = (mark) => {
      let snaps = null;
      try {
        snaps = JSON.parse(mark.dataset.snaps || 'null');
      } catch (e) {}
      return { url: mark.dataset.wb, md: mark.dataset.md, snaps, year: yearSel.value };
    };

    function show(entry) {
      clearTimeout(slowTimer);
      input.value = entry.url;
      box.querySelector('[data-web-yearlabel]').textContent = yearSel.value;
      box.querySelector('[data-web-act="back"]').disabled = at === 0;
      box.querySelector('[data-web-act="forward"]').disabled = at >= trail.length - 1;
      if (entry.url === HOME) {
        home.hidden = false;
        frame.hidden = true;
        loading.hidden = true;
        frame.removeAttribute('src');
        status.textContent = 'Start page';
        outs.forEach((o) => (o.hidden = o.closest('[data-web-loading]') ? false : true));
        return;
      }
      home.hidden = true;
      frame.hidden = false;
      loading.hidden = false;
      slow.hidden = true;
      box.querySelector('[data-web-when]').textContent = entry.year;
      status.textContent = `Connecting to ${entry.url.replace(/^https?:\/\//, '')} in ${entry.year}…`;
      outs.forEach((o) => {
        o.href = archived(entry, true);
        o.hidden = false;
      });
      frame.src = archived(entry);
      slowTimer = setTimeout(() => (slow.hidden = false), 12000);
    }

    function go(entry) {
      trail = trail.slice(0, at + 1).concat(entry);
      at = trail.length - 1;
      show(entry);
    }

    frame.addEventListener('load', () => {
      if (!frame.getAttribute('src')) return;
      clearTimeout(slowTimer);
      loading.hidden = true;
      const ts = snapFor(trail[at]).match(/^(\d{4})(\d{2})(\d{2})/);
      const e = trail[at];
      // Only a checked capture has a real date; a typed address the archive
      // couldn't look up is just the capture nearest the year asked for.
      const known = Boolean(e.snap || (e.snaps && Object.values(e.snaps).some(Boolean)));
      const when = ts ? new Date(Number(ts[1]), Number(ts[2]) - 1, Number(ts[3])).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' }) : '';
      status.textContent = known && when ? `From the Internet Archive · saved ${when}` : `From the Internet Archive · closest capture to ${e.year}`;
    });

    async function visit(entry) {
      if (entry.url === HOME) return go(entry);
      const known = bookmarks.find((b) => b.dataset.wb === entry.url);
      if (known) return go({ ...fromMark(known), year: entry.year });
      loading.hidden = false;
      home.hidden = true;
      go(await lookUp(entry));
    }
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      visit({ url: normalize(input.value), year: yearSel.value });
    });
    yearSel.addEventListener('change', () => {
      const cur = trail[at];
      box.querySelector('[data-web-yearlabel]').textContent = yearSel.value;
      if (cur.url !== HOME) visit({ url: cur.url, year: yearSel.value });
    });

    box.addEventListener('click', (e) => {
      const mark = e.target.closest('[data-wb]');
      if (mark) {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        go(fromMark(mark));
        return;
      }
      if (e.target.closest('[data-web-random]')) {
        go(fromMark(bookmarks[Math.floor(Math.random() * bookmarks.length)]));
        return;
      }
      const act = e.target.closest('[data-web-act]')?.dataset.webAct;
      if (act === 'back' && at > 0) show(trail[--at]);
      if (act === 'forward' && at < trail.length - 1) show(trail[++at]);
      if (act === 'reload') show(trail[at]);
      if (act === 'home') go({ url: HOME });
    });

    // Closing the window stops whatever old page was playing.
    box.closest('.os-win').addEventListener('os:close', (e) => {
      if (e.detail?.minimized) return;
      trail = [{ url: HOME }];
      at = 0;
      show(trail[0]);
    });
  }

  /*
    SportsVision: plays the guide's YouTube clips, a random one first. The
    IFrame API loads only when the TV is first opened, and the privacy host
    keeps YouTube cookies off until someone presses play.
  */
  let youtube;
  function loadYouTube() {
    youtube ||= new Promise((resolve) => {
      if (window.YT && window.YT.Player) return resolve();
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (previous) previous();
        resolve();
      };
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.append(tag);
    });
    return youtube;
  }

  function initTv(os) {
    const tv = os.querySelector('[data-tv]');
    const clips = tv ? [...tv.querySelectorAll('[data-tv-clip]')] : [];
    if (!clips.length) return;
    const win = tv.closest('.os-win');
    const idle = tv.querySelector('[data-tv-idle]');
    const idleText = tv.querySelector('[data-tv-idle-text]');
    const watch = tv.querySelector('[data-tv-watch]');
    let player = null;
    let ready = false;
    let pending = null;
    let current = -1;
    let picked = false;
    let failures = 0;

    // picked: the viewer chose this channel, so a blocked clip offers
    // YouTube instead of jumping somewhere they didn't ask for.
    function show(index, chosen = false) {
      current = (index + clips.length) % clips.length;
      picked = chosen;
      const clip = clips[current];
      tv.querySelector('[data-tv-ch]').textContent = `CH ${String(current + 1).padStart(2, '0')}`;
      tv.querySelector('[data-tv-title]').textContent = clip.dataset.title;
      tv.querySelector('[data-tv-meta]').textContent = clip.dataset.meta;
      clips.forEach((c) => c.setAttribute('aria-current', c === clip ? 'true' : 'false'));
      if (ready) idle.hidden = true;
      watch.hidden = true;
      const video = { videoId: clip.dataset.id, startSeconds: Number(clip.dataset.start) || 0 };
      if (ready) player.loadVideoById(video);
      else pending = video;
    }

    function shuffle() {
      let next = current;
      while (clips.length > 1 && next === current) next = Math.floor(Math.random() * clips.length);
      show(next);
    }

    function offerYouTube() {
      const clip = clips[current];
      const start = Number(clip.dataset.start) || 0;
      watch.href = `https://www.youtube.com/watch?v=${clip.dataset.id}${start ? `&t=${start}s` : ''}`;
      idleText.textContent = 'This channel only plays on YouTube';
      watch.hidden = false;
      idle.hidden = false;
    }

    async function start() {
      if (player) return;
      player = true;
      shuffle();
      await loadYouTube();
      player = new window.YT.Player(tv.querySelector('[data-tv-player]'), {
        host: 'https://www.youtube-nocookie.com',
        playerVars: { rel: 0, playsinline: 1, modestbranding: 1 },
        events: {
          onReady: (e) => {
            ready = true;
            idle.hidden = true;
            e.target.loadVideoById(pending);
            pending = null;
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) failures = 0;
            if (e.data === window.YT.PlayerState.ENDED) shuffle();
          },
          onError: () => {
            // Embedding turned off or the video is gone.
            if (!picked && ++failures < clips.length) show(current + 1);
            else offerYouTube();
          },
        },
      });
    }

    tv.addEventListener('click', (e) => {
      const act = e.target.closest('[data-tv-act]')?.dataset.tvAct;
      const listing = e.target.closest('[data-tv-clip]');
      if (!act && !listing) return;
      if (!player) start();
      if (act === 'next') show(current + 1, true);
      if (act === 'prev') show(current - 1, true);
      if (act === 'random') shuffle();
      if (listing) show(clips.indexOf(listing), true);
    });

    win.addEventListener('os:open', () => (player ? ready && player.playVideo() : start()));
    win.addEventListener('os:close', () => ready && player.pauseVideo());
  }

  /*
    Scoreboard: the teams listed in the section's setting, from ESPN's public
    scoreboard feeds. They're unofficial, so any failure just leaves a dash.
    Each league's board gives today's and live games; a team with nothing
    today falls back to its own feed for the next game. Polls only while the
    window is open: every 30 s with a game on, otherwise every 5 min.
  */
  function initScores(os) {
    const box = os.querySelector('[data-scores]');
    if (!box) return;
    const API = 'https://site.api.espn.com/apis/site/v2/sports';
    const win = box.closest('.os-win');
    const list = box.querySelector('[data-scores-list]');
    const stamp = box.querySelector('[data-scores-updated]');
    const rows = [...list.querySelectorAll('[data-team]')];
    const teamInfo = new Map();
    let timer = null;
    let showing = false;

    const json = (url) =>
      fetch(url).then((res) => {
        if (!res.ok) throw new Error(res.status);
        return res.json();
      });
    const abbr = (c) => (c.team.abbreviation || '').toLowerCase();
    const points = (c) => (c.score && typeof c.score === 'object' ? c.score.displayValue : c.score);
    const time = (iso) => {
      const d = new Date(iso);
      const today = new Date().toDateString() === d.toDateString();
      const day = today ? 'Today' : d.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric' });
      return `${day} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
    };

    function side(c, team, state) {
      const node = document.createElement('span');
      node.className = 'os-score__side';
      node.classList.toggle('is-mine', abbr(c) === team);
      node.classList.toggle('is-win', state === 'post' && Boolean(c.winner));
      node.append(c.team.abbreviation || '');
      if (state !== 'pre' && points(c) != null) {
        const b = document.createElement('b');
        b.textContent = points(c);
        node.append(' ', b);
      }
      return node;
    }

    function render(row, event, info) {
      const { team } = row.dataset;
      if (info) {
        row.style.setProperty('--team', `#${info.color || '888888'}`);
        row.querySelector('[data-score-record]').textContent = info.record?.items?.[0]?.summary || '';
      }
      const game = row.querySelector('[data-score-game]');
      const status = row.querySelector('[data-score-status]');
      if (!event) {
        game.replaceChildren();
        status.textContent = 'No game scheduled';
        row.dataset.state = 'none';
        row.dataset.sort = '9';
        return;
      }
      const comp = event.competitions[0];
      const st = comp.status.type;
      const away = comp.competitors.find((c) => c.homeAway === 'away') || comp.competitors[0];
      const home = comp.competitors.find((c) => c.homeAway === 'home') || comp.competitors[1];
      const at = document.createElement('span');
      at.className = 'os-score__at';
      at.textContent = '@';
      game.replaceChildren(side(away, team, st.state), at, side(home, team, st.state));
      status.textContent = st.state === 'pre' ? time(event.date) : st.shortDetail;
      row.dataset.state = st.state;
      // Live first, then today's finals (latest first), then what's next (soonest first).
      const t = new Date(event.date).getTime() / 1e13;
      row.dataset.sort = st.state === 'in' ? '0' : st.state === 'post' ? String(2 - t) : String(4 + t);
    }

    async function refresh() {
      clearTimeout(timer);
      stamp.textContent = 'Checking the wires…';
      const leagues = [...new Set(rows.map((r) => r.dataset.league))];
      const boards = {};
      let failures = 0;
      await Promise.all(
        leagues.map(async (league) => {
          try {
            boards[league] = (await json(`${API}/${league}/scoreboard`)).events || [];
          } catch (e) {
            boards[league] = [];
            failures++;
          }
        })
      );
      await Promise.all(
        rows.map(async (row) => {
          const { league, team } = row.dataset;
          let event = boards[league].find((e) => e.competitions[0].competitors.some((c) => abbr(c) === team));
          let info = teamInfo.get(league + team);
          if (!info || !event) {
            try {
              info = (await json(`${API}/${league}/teams/${team}`)).team;
              teamInfo.set(league + team, info);
            } catch (e) {
              failures++;
            }
          }
          if (!event && info?.nextEvent?.length) event = info.nextEvent[0];
          render(row, event, info);
        })
      );
      rows.sort((a, b) => Number(a.dataset.sort) - Number(b.dataset.sort)).forEach((r) => list.append(r));
      const live = rows.some((r) => r.dataset.state === 'in');
      stamp.textContent =
        failures >= leagues.length + rows.length
          ? 'Scores are unavailable right now.'
          : `Updated ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}${live ? ' · live' : ''}`;
      if (showing) timer = setTimeout(refresh, live ? 30000 : 300000);
    }

    box.querySelector('[data-scores-refresh]').addEventListener('click', refresh);
    win.addEventListener('os:open', () => {
      showing = true;
      refresh();
    });
    win.addEventListener('os:close', () => {
      showing = false;
      clearTimeout(timer);
    });
  }

  /*
    Five Borough FM: an FM dial from 87.5 to 108 with the section's station
    blocks on it. Stations with a public stream play here; the rest sit on
    the dial and hand off to their own app. Between stations it's static,
    made in the browser. Nothing sounds until Power is pressed; closing the
    window turns it off, minimizing keeps it playing.
  */
  function initRadio(os) {
    const box = os.querySelector('[data-radio]');
    if (!box) return;
    const LO = 87.5;
    const HI = 108;
    const win = box.closest('.os-win');
    const dial = box.querySelector('[data-radio-dial]');
    const $ = (sel) => box.querySelector(sel);
    const power = $('[data-radio-power]');
    const volume = $('[data-radio-volume]');
    const link = $('[data-radio-link]');
    const stations = [...box.querySelectorAll('[data-station]')]
      .map((node) => ({ el: node, f: Number(node.dataset.freq), ...node.dataset }))
      .filter((s) => s.f >= LO && s.f <= HI)
      .sort((a, b) => a.f - b.f);
    if (!stations.length) return;
    const audio = new Audio();
    audio.preload = 'none';
    let ctx = null;
    let hiss = null;
    let hissAt = 0;
    let on = false;
    let current;
    let freq = stations[0].f;
    try {
      freq = Number(localStorage.getItem('hd-radio')) || freq;
    } catch (e) {}

    const pos = (f) => ((f - LO) / (HI - LO)) * 100;
    stations.forEach((s) => {
      const mark = document.createElement('span');
      mark.className = 'os-radio__mark';
      mark.style.left = `${pos(s.f)}%`;
      $('[data-radio-marks]').append(mark);
    });

    function startHiss() {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!ctx) {
        ctx = new Ctx();
        const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        const noise = ctx.createBufferSource();
        noise.buffer = buffer;
        noise.loop = true;
        const band = ctx.createBiquadFilter();
        band.type = 'bandpass';
        band.frequency.value = 1800;
        band.Q.value = 0.6;
        hiss = ctx.createGain();
        hiss.gain.value = 0;
        noise.connect(band).connect(hiss).connect(ctx.destination);
        noise.start();
      }
      ctx.resume();
    }

    function setHiss(level) {
      hissAt = level;
      if (hiss) hiss.gain.setTargetAtTime(on ? level * Number(volume.value) * 0.22 : 0, ctx.currentTime, 0.04);
    }

    function play() {
      $('[data-radio-state]').textContent = 'Tuning in…';
      audio.src = current.stream; // a fresh request, so it's live rather than where it paused
      audio.play().catch(() => {
        if (on) $('[data-radio-state]').textContent = 'Can’t reach the station';
      });
    }

    function switchTo(station) {
      current = station;
      audio.pause();
      $('[data-radio-call]').textContent = station ? station.call : '';
      $('[data-radio-name]').textContent = station ? station.name : on ? 'Between stations' : 'Press power to listen';
      stations.forEach((s) => s.el.setAttribute('aria-pressed', s === station ? 'true' : 'false'));
      link.hidden = !station;
      if (station) {
        link.href = station.link;
        link.textContent = station.stream ? `Visit ${station.call}` : `Listen on ${station.via || 'their site'}`;
      }
      if (!on) $('[data-radio-state]').textContent = 'Off';
      else if (!station) $('[data-radio-state]').textContent = 'Static';
      else if (!station.stream) $('[data-radio-state]').textContent = `Plays on ${station.via || 'its own site'}`;
      else play();
    }

    function tune(f, settle = false) {
      freq = Math.round(Math.min(HI, Math.max(LO, f)) * 10) / 10;
      const near = stations.reduce((a, b) => (Math.abs(b.f - freq) < Math.abs(a.f - freq) ? b : a));
      if (settle && Math.abs(near.f - freq) <= 0.3) freq = near.f;
      const off = Math.abs(near.f - freq);
      const station = off < 0.05 ? near : null;
      dial.style.setProperty('--p', String(pos(freq) / 100));
      dial.setAttribute('aria-valuenow', freq);
      dial.setAttribute('aria-valuetext', `${freq.toFixed(1)} FM${station ? `, ${station.call}` : ''}`);
      $('[data-radio-freq]').textContent = freq.toFixed(1);
      if (station !== current) switchTo(station);
      // Clear on a streaming station, faint under a link-only one, loud in between.
      setHiss(station ? (station.stream ? 0 : 0.35) : Math.min(1, 0.45 + off));
      if (settle) {
        try {
          localStorage.setItem('hd-radio', String(freq));
        } catch (e) {}
      }
    }

    function setPower(next) {
      on = next;
      box.classList.toggle('is-on', on);
      power.setAttribute('aria-pressed', String(on));
      if (on) {
        startHiss();
        current = undefined;
        tune(freq, true);
      } else {
        audio.pause();
        setHiss(0);
        if (ctx) ctx.suspend();
        switchTo(current || null);
      }
    }

    function seek(dir) {
      const next =
        dir > 0
          ? stations.find((s) => s.f > freq + 0.05) || stations[0]
          : [...stations].reverse().find((s) => s.f < freq - 0.05) || stations[stations.length - 1];
      tune(next.f, true);
    }

    audio.addEventListener('playing', () => {
      if (!on) return;
      $('[data-radio-state]').textContent = 'On air';
      box.classList.add('is-playing');
    });
    ['pause', 'waiting', 'error', 'emptied'].forEach((type) => audio.addEventListener(type, () => box.classList.remove('is-playing')));
    audio.addEventListener('waiting', () => on && ($('[data-radio-state]').textContent = 'Tuning in…'));
    audio.addEventListener('error', () => on && current?.stream && ($('[data-radio-state]').textContent = 'Can’t reach the station'));

    power.addEventListener('click', () => setPower(!on));
    box.querySelectorAll('[data-radio-seek]').forEach((b) =>
      b.addEventListener('click', () => {
        if (!on) setPower(true);
        seek(Number(b.dataset.radioSeek));
      })
    );
    stations.forEach((s) =>
      s.el.addEventListener('click', () => {
        if (!on) setPower(true);
        tune(s.f, true);
      })
    );
    volume.addEventListener('input', () => {
      audio.volume = Number(volume.value);
      setHiss(hissAt);
    });

    const fromPointer = (e) => {
      const r = dial.querySelector('.os-radio__ticks').getBoundingClientRect();
      return LO + ((e.clientX - r.left) / r.width) * (HI - LO);
    };
    dial.addEventListener('pointerdown', (e) => {
      if (!on) setPower(true);
      dial.setPointerCapture(e.pointerId);
      tune(fromPointer(e));
      const move = (ev) => tune(fromPointer(ev));
      const up = () => {
        tune(freq, true);
        dial.removeEventListener('pointermove', move);
        dial.removeEventListener('pointerup', up);
        dial.removeEventListener('pointercancel', up);
      };
      dial.addEventListener('pointermove', move);
      dial.addEventListener('pointerup', up);
      dial.addEventListener('pointercancel', up);
    });
    dial.addEventListener('keydown', (e) => {
      const step = { ArrowLeft: -0.1, ArrowDown: -0.1, ArrowRight: 0.1, ArrowUp: 0.1 }[e.key];
      // Arrows fine-tune without snapping, so they can walk off a station into static.
      if (step) tune(freq + step);
      else if (e.key === 'PageUp') seek(1);
      else if (e.key === 'PageDown') seek(-1);
      else if (e.key === 'Home') tune(LO, true);
      else if (e.key === 'End') tune(HI, true);
      else return;
      e.preventDefault();
    });

    win.addEventListener('os:close', (e) => {
      if (!e.detail?.minimized && on) setPower(false);
    });

    tune(freq);
  }

  /*
    Hat Trick Messenger: sign on with a screen name and an email, pick a
    buddy, IM. Lines to HatTrickNYC collect for a few seconds and go out as
    one contact-form email (flushed on page hide too), so a burst of short
    IMs doesn't become a burst of emails. HatTrickNYC answers the first IM
    with an auto response: its online line from 9 AM to 9 PM New York time,
    its away message otherwise. Team buddies are always away.
  */
  function initMessenger(os, open) {
    const box = os.querySelector('[data-im]');
    const chat = os.querySelector('[data-chat]');
    if (!box || !chat) return;
    const KEY = 'os-im';
    const HT = 'HatTrickNYC';
    const listWin = box.closest('.os-win');
    const chatWin = chat.closest('.os-win');
    const signon = box.querySelector('[data-im-signon]');
    const list = box.querySelector('[data-im-list]');
    const profile = box.querySelector('[data-im-profile]');
    const buddies = [...box.querySelectorAll('[data-buddy]')];
    const logEl = chat.querySelector('[data-chat-log]');
    const input = chat.querySelector('[data-chat-input]');
    const note = chat.querySelector('[data-chat-note]');
    const sendBtn = chat.querySelector('[data-chat-send]');
    let me = { name: '', email: '', log: [] };
    let selected = null;
    let talkingTo = HT;
    let pending = [];
    let flushTimer = null;
    let lastAuto = 0;
    try {
      me = { ...me, ...JSON.parse(store.get(KEY) || '{}') };
    } catch (e) {}
    const save = () => store.set(KEY, JSON.stringify({ ...me, log: me.log.slice(-60) }));

    const nyHour = () => Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: 'America/New_York' }).format(new Date()));
    const htOnline = () => {
      const h = nyHour();
      return h >= 9 && h < 21;
    };
    const fill = (text) => text.replace(/\{email\}/g, me.email || 'your email');
    const stamp = (t) => new Date(t).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

    function showList() {
      const signedOn = Boolean(me.name && me.email);
      signon.hidden = signedOn;
      list.hidden = !signedOn;
      if (!signedOn) {
        signon.elements.name.value = me.name || '';
        signon.elements.email.value = me.email || '';
        return;
      }
      box.querySelector('[data-im-me]').textContent = me.name;
      const ht = buddies.find((b) => b.dataset.buddy === HT);
      ht.classList.toggle('is-away', !htOnline());
      box.querySelector('[data-im-ht-count]').textContent = '1';
    }

    function select(b) {
      selected = b;
      buddies.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    }

    function info(b) {
      if (!b) return (profile.textContent = 'Pick a buddy, then IM or Info.');
      if (b.dataset.buddy === HT) {
        profile.textContent = `${b.dataset.profile} ${htOnline() ? 'Online now.' : 'Away right now.'}`;
      } else {
        profile.textContent = `${b.dataset.buddy} is away: “${b.dataset.away}”`;
      }
    }

    function line(entry) {
      const p = el('p', { className: `os-chat__line is-${entry.who}${entry.auto ? ' is-auto' : ''}` });
      if (entry.who === 'sys') p.textContent = entry.text;
      else {
        p.append(
          el('span', { className: 'os-chat__who', textContent: entry.name }),
          el('span', { className: 'os-chat__time', textContent: ` (${stamp(entry.t)})` }),
          `: ${entry.text}`
        );
      }
      return p;
    }

    function renderChat(extra = []) {
      const entries = talkingTo === HT ? me.log : extra;
      logEl.replaceChildren(...entries.map(line));
      logEl.scrollTop = logEl.scrollHeight;
    }

    function say(entry) {
      me.log.push({ t: Date.now(), ...entry });
      save();
      if (talkingTo === HT) renderChat();
    }

    function openChat(b) {
      if (!b) return;
      talkingTo = b.dataset.buddy;
      chatWin.querySelector('[data-win-title]').textContent = `${talkingTo}: Instant Message`;
      const team = talkingTo !== HT;
      input.disabled = team;
      sendBtn.disabled = team;
      note.textContent = team ? `Only ${HT} gets messages.` : '';
      if (team) renderChat([{ who: 'them', auto: true, name: talkingTo, text: b.dataset.away, t: Date.now() }]);
      else renderChat();
      open('im', { user: true });
      if (!team) input.focus({ preventScroll: true });
    }

    function formFor(text) {
      const data = new FormData();
      data.append('form_type', 'contact');
      data.append('utf8', '✓');
      data.append('contact[name]', me.name);
      data.append('contact[email]', me.email);
      data.append('contact[Subject]', 'Instant message');
      data.append('contact[Sent from]', 'Hat Trick OS Messenger');
      data.append('contact[body]', text);
      return data;
    }

    const batch = () => pending.map((p) => `[${stamp(p.t)}] ${p.text}`).join('\n');

    // Couldn't send in place (a spam check or a network hiccup): hand the
    // lines to Mail so nothing is lost.
    function toMail(text) {
      const form = os.querySelector('#os-mail-form');
      if (!form) return;
      form.elements['contact[name]'].value = me.name;
      form.elements['contact[email]'].value = me.email;
      form.elements['contact[body]'].value = text;
      open('mail', { user: true });
    }

    async function flush() {
      clearTimeout(flushTimer);
      if (!pending.length) return;
      const text = batch();
      const sent = pending;
      pending = [];
      note.textContent = 'Sending…';
      try {
        const res = await fetch(`${ROOT}contact`, { method: 'POST', body: formFor(text), credentials: 'same-origin' });
        if (/\/challenge/.test(res.url)) throw new Error('challenge');
        if (!res.ok || !/contact_posted=true/.test(res.url)) throw new Error(res.status);
        note.textContent = `Delivered to our inbox · ${stamp(Date.now())}`;
      } catch (err) {
        pending = sent.concat(pending);
        note.replaceChildren(
          'Didn’t go through. ',
          el('button', { type: 'button', className: 'os-textbtn', textContent: 'Send with Mail', onclick: () => {
            const t = batch();
            pending = [];
            note.textContent = '';
            toMail(t);
          } })
        );
      }
    }

    // A page closing mid-batch still sends it.
    window.addEventListener('pagehide', () => {
      if (!pending.length || !navigator.sendBeacon) return;
      navigator.sendBeacon(`${ROOT}contact`, formFor(batch()));
      pending = [];
    });

    signon.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!signon.reportValidity()) return;
      me.name = signon.elements.name.value.trim().slice(0, 24);
      me.email = signon.elements.email.value.trim();
      if (!me.log.length) say({ who: 'sys', text: `${me.name} signed on at ${stamp(Date.now())}` });
      save();
      showList();
    });

    box.addEventListener('click', (e) => {
      const b = e.target.closest('[data-buddy]');
      if (b) {
        select(b);
        info(b);
        return;
      }
      const act = e.target.closest('[data-im-act]')?.dataset.imAct;
      if (act === 'im') openChat(selected || buddies[0]);
      if (act === 'info') info(selected || buddies[0]);
      if (act === 'signoff') {
        flush();
        me = { name: '', email: '', log: [] };
        store.set(KEY, '{}');
        select(null);
        info(null);
        showList();
        if (!chatWin.hidden) chatWin.querySelector('[data-act="close"]').click();
      }
    });
    box.addEventListener('dblclick', (e) => {
      const b = e.target.closest('[data-buddy]');
      if (b) openChat(b);
    });
    box.addEventListener('keydown', (e) => {
      const b = e.target.closest('[data-buddy]');
      if (b && e.key === 'Enter') {
        e.preventDefault();
        select(b);
        openChat(b);
      }
    });

    chat.querySelector('[data-chat-form]').addEventListener('submit', (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text || talkingTo !== HT) return;
      if (!me.name || !me.email) {
        open('messenger', { user: true });
        return;
      }
      input.value = '';
      say({ who: 'me', name: me.name, text });
      pending.push({ t: Date.now(), text });
      clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 6000);
      note.textContent = 'Typing is fine, we’ll send it in a sec…';
      if (Date.now() - lastAuto > 15 * 60 * 1000) {
        lastAuto = Date.now();
        const online = htOnline();
        say({ who: 'them', auto: true, name: HT, text: fill(online ? box.dataset.online : box.dataset.away) });
      }
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        chat.querySelector('[data-chat-form]').requestSubmit();
      }
    });

    chat.addEventListener('click', (e) => {
      const act = e.target.closest('[data-chat-act]')?.dataset.chatAct;
      if (!act) return;
      const text =
        act === 'warn'
          ? `You can’t warn ${talkingTo}. ${talkingTo}’s warning level is still 0%.`
          : `Blocking ${talkingTo}? Bold move. Not today.`;
      if (talkingTo === HT) say({ who: 'sys', text });
      else logEl.append(line({ who: 'sys', text }));
    });

    chatWin.addEventListener('os:close', (e) => e.detail?.minimized || flush());
    listWin.addEventListener('os:open', showList);
    showList();
  }

  /*
    Mail: sends the compose window through Shopify's contact form without
    leaving the desktop. Shopify answers a good post by redirecting to
    ?contact_posted=true. If it wants a spam check (/challenge) the form
    posts normally instead, and the section reopens Mail with the result.
  */
  function initMail(os) {
    const form = os.querySelector('#os-mail-form');
    if (!form) return;
    const status = form.querySelector('[data-mail-status]');
    const send = form.querySelector('[data-mail-send]');
    const say = (text, kind = '') => {
      status.textContent = text;
      status.className = `os-mail__status${kind ? ` is-${kind}` : ''}`;
    };
    if (/contact_posted=true/.test(location.search)) status.classList.add('is-sent');
    else if (status.textContent.trim()) status.classList.add('is-error');

    form.addEventListener('submit', async (e) => {
      if (form.dataset.native) return;
      e.preventDefault();
      if (!form.reportValidity()) return;
      send.disabled = true;
      say('Sending…');
      try {
        const res = await fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' });
        if (/\/challenge/.test(res.url)) {
          form.dataset.native = '1';
          form.submit();
          return;
        }
        if (!res.ok || !/contact_posted=true/.test(res.url)) throw new Error(res.status);
        form.querySelector('textarea').value = '';
        say('Message sent. We’ll write back soon.', 'sent');
      } catch (err) {
        say('That didn’t send. Check your email address and try again, or use the Contact page.', 'error');
      } finally {
        send.disabled = false;
      }
    });

    form.addEventListener('input', () => status.classList.contains('is-sent') && say(''));
  }

  /*
    Subway Series Solitaire: Klondike. Drag a card or stack onto a pile, or
    click (Enter or Space) a card to send it to the best move: foundation
    first, then a column. The deal, the draw setting and wins are kept in
    localStorage 'os-solitaire', so a game survives closing the window.
    Nothing animates; cards just land.
  */
  function initSolitaire(os) {
    const box = os.querySelector('[data-sol]');
    if (!box) return;
    const KEY = 'os-solitaire';
    const SUITS = ['S', 'H', 'C', 'D'];
    const SYMBOL = { S: '♠', H: '♥', C: '♣', D: '♦' };
    const SUIT_NAME = { S: 'spades', H: 'hearts', C: 'clubs', D: 'diamonds' };
    const RANK = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
    const RANK_NAME = ['', 'Ace', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Jack', 'Queen', 'King'];
    // Face cards wear the hat: the King a crown, the Queen a star, the Jack a baseball.
    const CAP =
      '<path d="M8 30c0-9 5-15 12-15s12 6 12 15z"/><path d="M4 31c8-3 24-3.5 34 0l-1.5 4c-10-3-22-3-31 0z"/>' +
      '<path class="ht-stitch" d="M20 15v15M14 18c-1.5 4-1.5 8 0 12M26 18c1.5 4 1.5 8 0 12"/>';
    const FACE = {
      11: '<circle cx="20" cy="7" r="5"/><path class="ht-stitch" d="M16.5 4c1.5 2 1.5 4 0 6M23.5 4c-1.5 2-1.5 4 0 6"/>',
      12: '<path d="M20 1l2.2 4.6 5 .6-3.7 3.4 1 4.9L20 12l-4.5 2.5 1-4.9L12.8 6.2l5-.6z"/>',
      13: '<path d="M11 13l2-9 4 4.5L20 2l3 6.5L27 4l2 9z"/>',
    };
    const red = (c) => c.s === 'H' || c.s === 'D';
    const table = box.querySelector('[data-sol-table]');
    const win = box.closest('.os-win');
    const piles = {};
    table.querySelectorAll('[data-pile]').forEach((p) => (piles[p.dataset.pile] = p));
    const undoBtn = box.querySelector('[data-sol-act="undo"]');
    const finishBtn = box.querySelector('[data-sol-act="finish"]');
    const drawSel = box.querySelector('[data-sol-draw]');
    const stats = box.querySelector('[data-sol-stats]');
    const winBox = box.querySelector('[data-sol-win]');
    let game = null;
    let tally = { wins: 0, played: 0 };
    let history = [];
    let refocus = null;
    let keyboard = false;

    try {
      const saved = JSON.parse(store.get(KEY) || 'null');
      if (saved && saved.game && saved.game.t) game = saved.game;
      if (saved && saved.tally) tally = saved.tally;
    } catch (e) {}
    drawSel.value = String((game && game.draw) || box.dataset.draw || 1);

    const save = () => store.set(KEY, JSON.stringify({ game, tally }));
    const get = (name) => (name === 'stock' ? game.stock : name === 'waste' ? game.waste : game[name[0]][Number(name.slice(1))]);
    const snap = () => JSON.stringify({ stock: game.stock, waste: game.waste, f: game.f, t: game.t, moves: game.moves });
    const remember = () => {
      history.push(snap());
      if (history.length > 300) history.shift();
    };

    function deal() {
      const deck = [];
      SUITS.forEach((s) => {
        for (let r = 1; r <= 13; r++) deck.push({ s, r, up: false });
      });
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }
      const t = Array.from({ length: 7 }, () => []);
      for (let i = 0; i < 7; i++) for (let j = i; j < 7; j++) t[j].push(deck.pop());
      t.forEach((p) => (p[p.length - 1].up = true));
      game = { stock: deck, waste: [], f: [[], [], [], []], t, moves: 0, draw: Number(drawSel.value) || 1, won: false };
      history = [];
      tally.played++;
      save();
      render();
    }

    function canDrop(cards, target) {
      const c = cards[0];
      const pile = get(target);
      const top = pile[pile.length - 1];
      if (target[0] === 'f') return cards.length === 1 && (top ? top.s === c.s && top.r === c.r - 1 : c.r === 1);
      if (target[0] === 't') return top ? top.up && red(top) !== red(c) && top.r === c.r + 1 : c.r === 13;
      return false;
    }

    function settle() {
      if (!game.won && game.f.every((p) => p.length === 13)) {
        game.won = true;
        tally.wins++;
      }
      save();
      render();
    }

    function move(from, index, to) {
      remember();
      const src = get(from);
      const cards = src.splice(index);
      get(to).push(...cards);
      const top = src[src.length - 1];
      if (from[0] === 't' && top && !top.up) top.up = true;
      game.moves++;
      refocus = `${cards[0].s}${cards[0].r}`;
      settle();
      return true;
    }

    function drawCards() {
      if (!game.stock.length && !game.waste.length) return;
      remember();
      if (game.stock.length) {
        const n = Math.min(game.draw, game.stock.length);
        for (let i = 0; i < n; i++) {
          const c = game.stock.pop();
          c.up = true;
          game.waste.push(c);
        }
      } else {
        game.stock = game.waste.reverse().map((c) => ({ ...c, up: false }));
        game.waste = [];
      }
      game.moves++;
      settle();
    }

    const columns = (from) => ['t0', 't1', 't2', 't3', 't4', 't5', 't6'].filter((t) => t !== from);

    // The best move for a click: home to a foundation, then onto a column,
    // then a King into an empty column (unless it's already at the top of one).
    function autoMove(from, index) {
      const cards = get(from).slice(index);
      if (!cards.length || !cards[0].up) return false;
      if (cards.length === 1 && from[0] !== 'f') {
        for (let i = 0; i < 4; i++) if (canDrop(cards, `f${i}`)) return move(from, index, `f${i}`);
      }
      for (const t of columns(from)) if (get(t).length && canDrop(cards, t)) return move(from, index, t);
      if (!(from[0] === 't' && index === 0)) {
        for (const t of columns(from)) if (!get(t).length && canDrop(cards, t)) return move(from, index, t);
      }
      return false;
    }

    const finishable = () => !game.won && !game.stock.length && !game.waste.length && game.t.every((p) => p.every((c) => c.up));

    function finish() {
      remember();
      let moved = true;
      while (moved) {
        moved = false;
        for (let k = 0; k < 7; k++) {
          const p = game.t[k];
          const c = p[p.length - 1];
          if (!c) continue;
          for (let i = 0; i < 4; i++) {
            if (canDrop([c], `f${i}`)) {
              game.f[i].push(p.pop());
              game.moves++;
              moved = true;
              break;
            }
          }
        }
      }
      settle();
    }

    function cardEl(c, from, index, x, y) {
      const node = document.createElement('div');
      node.className = 'os-card';
      node.style.setProperty('--x', x);
      node.style.setProperty('--y', y);
      if (!c.up) {
        node.classList.add('is-down');
        node.setAttribute('aria-hidden', 'true');
        return node;
      }
      node.classList.toggle('is-red', red(c));
      node.dataset.from = from;
      node.dataset.index = index;
      node.dataset.key = `${c.s}${c.r}`;
      node.tabIndex = 0;
      node.setAttribute('role', 'button');
      node.setAttribute('aria-label', `${RANK_NAME[c.r]} of ${SUIT_NAME[c.s]}`);
      const corner = `<span class="os-card__corner" aria-hidden="true">${RANK[c.r]}<span>${SYMBOL[c.s]}</span></span>`;
      const pip = c.r > 10 ? `<svg viewBox="0 0 40 40">${CAP}${FACE[c.r]}</svg>` : SYMBOL[c.s];
      node.innerHTML = `${corner}<span class="os-card__pip" aria-hidden="true">${pip}</span>${corner.replace('os-card__corner"', 'os-card__corner os-card__corner--b"')}`;
      return node;
    }

    function render() {
      if (!game) return;
      Object.values(piles).forEach((p) => p.replaceChildren());
      if (game.stock.length) piles.stock.append(cardEl(game.stock[game.stock.length - 1], 'stock', 0, 0, 0));
      piles.stock.setAttribute('aria-label', game.stock.length ? `Deal from the stock, ${game.stock.length} left` : 'Turn the waste back over');

      const fan = game.draw === 3 ? 3 : 2;
      const shown = game.waste.slice(-fan);
      shown.forEach((c, i) => {
        const index = game.waste.length - shown.length + i;
        const node = cardEl(c, 'waste', index, game.draw === 3 ? i * 0.22 : 0, 0);
        if (index !== game.waste.length - 1) {
          node.tabIndex = -1;
          node.style.pointerEvents = 'none';
        }
        piles.waste.append(node);
      });

      game.f.forEach((p, i) => {
        const shownF = p.slice(-2);
        shownF.forEach((c, k) => {
          const index = p.length - shownF.length + k;
          const node = cardEl(c, `f${i}`, index, 0, 0);
          if (index !== p.length - 1) node.tabIndex = -1;
          piles[`f${i}`].append(node);
        });
      });

      game.t.forEach((p, i) => {
        let y = 0;
        p.forEach((c, k) => {
          piles[`t${i}`].append(cardEl(c, `t${i}`, k, 0, y.toFixed(3)));
          if (k < p.length - 1) y += c.up ? 0.34 : 0.16;
        });
        piles[`t${i}`].style.setProperty('--h', (y + 1.4).toFixed(3));
      });

      undoBtn.disabled = history.length === 0;
      finishBtn.hidden = !finishable();
      stats.textContent = `Moves ${game.moves} · Wins ${tally.wins}`;
      winBox.hidden = !game.won;
      if (game.won) box.querySelector('[data-sol-winmoves]').textContent = `${game.moves} moves. That’s a clean sweep.`;
      if (refocus) {
        const again = table.querySelector(`[data-key="${refocus}"]`);
        if (again && keyboard) again.focus({ preventScroll: true });
        refocus = null;
      }
    }

    function nope(node) {
      node.classList.add('is-nope');
      setTimeout(() => node.classList.remove('is-nope'), 350);
    }

    // Card size follows the table's width: seven columns and six gaps.
    const fit = () => {
      const cs = getComputedStyle(table);
      const w = table.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const gap = parseFloat(cs.getPropertyValue('--gap')) || 10;
      table.style.setProperty('--cw', `${Math.max(34, Math.min(84, Math.floor((w - gap * 6) / 7)))}px`);
    };
    if (window.ResizeObserver) new ResizeObserver(fit).observe(table);
    fit();

    table.addEventListener('keydown', (e) => {
      const node = e.target.closest('.os-card[data-from]');
      if (!node || (e.key !== 'Enter' && e.key !== ' ')) return;
      e.preventDefault();
      keyboard = true;
      if (!autoMove(node.dataset.from, Number(node.dataset.index))) nope(node);
    });

    piles.stock.addEventListener('click', () => {
      keyboard = false;
      drawCards();
    });

    // Drag a card, or a column's face-up stack, onto the pile it overlaps most.
    function targetFor(node, cards, from) {
      const r = node.getBoundingClientRect();
      let best = null;
      let bestArea = 0;
      Object.keys(piles).forEach((name) => {
        if (name === from || (name[0] !== 't' && name[0] !== 'f')) return;
        const p = piles[name].getBoundingClientRect();
        const area =
          Math.max(0, Math.min(r.right, p.right) - Math.max(r.left, p.left)) *
          Math.max(0, Math.min(r.bottom, p.bottom) - Math.max(r.top, p.top));
        if (area > bestArea && canDrop(cards, name)) {
          best = name;
          bestArea = area;
        }
      });
      return best;
    }

    table.addEventListener('pointerdown', (e) => {
      const node = e.target.closest('.os-card[data-from]');
      if (!node || e.button !== 0) return;
      keyboard = false;
      const from = node.dataset.from;
      const index = Number(node.dataset.index);
      const cards = get(from).slice(index);
      const stack =
        from[0] === 't' ? [...piles[from].querySelectorAll('.os-card[data-from]')].filter((c) => Number(c.dataset.index) >= index) : [node];
      const sx = e.clientX;
      const sy = e.clientY;
      let dragging = false;
      let lit = null;
      node.setPointerCapture(e.pointerId);
      const light = (name) => {
        if (lit === name) return;
        if (lit) piles[lit].classList.remove('is-target');
        lit = name;
        if (lit) piles[lit].classList.add('is-target');
      };
      const onMove = (ev) => {
        const dx = ev.clientX - sx;
        const dy = ev.clientY - sy;
        if (!dragging && Math.hypot(dx, dy) < 5) return;
        dragging = true;
        stack.forEach((c) => {
          c.classList.add('is-lifted');
          c.style.transform = `translate(${dx}px, ${dy}px)`;
        });
        light(targetFor(node, cards, from));
      };
      const onUp = () => {
        node.removeEventListener('pointermove', onMove);
        node.removeEventListener('pointerup', onUp);
        node.removeEventListener('pointercancel', onUp);
        const target = dragging ? targetFor(node, cards, from) : null;
        light(null);
        if (!dragging) {
          if (!autoMove(from, index)) nope(node);
        } else if (target) move(from, index, target);
        else render();
      };
      node.addEventListener('pointermove', onMove);
      node.addEventListener('pointerup', onUp);
      node.addEventListener('pointercancel', onUp);
    });

    box.addEventListener('click', (e) => {
      const button = e.target.closest('[data-sol-act]');
      const act = button?.dataset.solAct;
      if (act === 'new') deal();
      if (act === 'undo' && history.length) {
        if (game.won) tally.wins = Math.max(0, tally.wins - 1);
        Object.assign(game, JSON.parse(history.pop()), { won: false });
        save();
        render();
      }
      if (act === 'finish') finish();
      if (act === 'copy') {
        const code = box.dataset.code || '';
        (navigator.clipboard ? navigator.clipboard.writeText(code) : Promise.reject(new Error('no clipboard')))
          .then(() => (button.textContent = 'Copied'))
          .catch(() => (button.textContent = 'Select the code to copy it'));
      }
    });

    drawSel.addEventListener('change', () => {
      if (!game) return;
      game.draw = Number(drawSel.value) || 1;
      save();
      render();
    });

    win.addEventListener('os:open', () => (game ? render() : deal()));
    if (game) render();
  }

  /*
    Comeback Ballot: sections/archive-comeback-ballot.liquid's voting in a
    window. Same Worker and the same session key, so a Shop login on either
    page counts on both, and the Worker still allows one ballot per account.
  */
  function initBallot(os, open) {
    const box = os.querySelector('[data-ballot]');
    if (!box) return;
    const KEY = 'ht_off_track_session';
    const api = box.dataset.api;
    const list = box.querySelector('[data-ballot-list]');
    const cands = [...list.querySelectorAll('[data-cand]')];
    const login = box.querySelector('[data-ballot-login]');
    const copy = box.querySelector('[data-ballot-copy]');
    const status = box.querySelector('[data-ballot-status]');
    let session = '';
    let selection = null;
    let standings = [];
    let loaded = false;
    try {
      session = localStorage.getItem(KEY) || '';
    } catch (e) {}

    const back = new URL(location.href);
    back.hash = '';
    if ((box.dataset.themeRole || '').toLowerCase() !== 'main') back.searchParams.set('preview_theme_id', box.dataset.themeId);
    else back.searchParams.delete('preview_theme_id');
    login.href = `${api}/auth/start?return=${encodeURIComponent(back.toString())}`;

    async function call(path, options = {}) {
      options.headers = { ...(options.headers || {}) };
      if (session) options.headers.Authorization = `Bearer ${session}`;
      const res = await fetch(api + path, options);
      const data = await res.json();
      if (!res.ok || data.ok === false) {
        const err = new Error(data.error || 'Voting is unavailable right now.');
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    }

    function render() {
      const votes = new Map(standings.map((s) => [s.handle, Number(s.votes) || 0]));
      // Like the ballot page, only hats still on the ballot count toward the share.
      const total = cands.reduce((sum, c) => sum + (votes.get(c.dataset.cand) || 0), 0);
      cands
        .map((c, i) => ({ c, i, v: votes.get(c.dataset.cand) || 0 }))
        .sort((a, b) => b.v - a.v || a.i - b.i)
        .forEach(({ c, v }) => {
          const pct = total ? Math.round((v / total) * 100) : 0;
          c.querySelector('[data-cand-pct]').textContent = `${pct}%`;
          c.querySelector('[data-cand-bar]').style.width = `${pct}%`;
          const mine = c.dataset.cand === selection;
          c.classList.toggle('is-mine', mine);
          const button = c.querySelector('[data-cand-vote]');
          button.disabled = Boolean(selection);
          button.textContent = mine ? 'Your Pick' : 'Vote';
          list.append(c);
        });
      box.classList.toggle('is-signed-in', Boolean(session));
      copy.textContent = selection
        ? 'Your ballot is counted and follows your Shop account.'
        : session
          ? 'Shop account verified. Pick one hat.'
          : 'Log in with Shop to cast your vote.';
      // Shares only: the ballot never shows how many votes have been cast.
      status.textContent = '';
    }

    async function load() {
      try {
        const board = await call('/archive/leaderboard');
        const mine = session ? await call('/archive/status') : { voted: false };
        standings = board.leaderboard || [];
        selection = mine.voted ? mine.candidate : null;
        loaded = true;
        render();
      } catch (err) {
        if (err.status === 401) {
          session = '';
          try {
            localStorage.removeItem(KEY);
          } catch (e) {}
        }
        status.textContent = err.status ? err.message : 'The ballot box can’t be reached from here right now.';
      }
    }

    list.addEventListener('click', async (e) => {
      const button = e.target.closest('[data-cand-vote]');
      if (!button || selection) return;
      if (!session) {
        location.href = login.href;
        return;
      }
      const cand = button.closest('[data-cand]');
      button.disabled = true;
      button.textContent = 'Counting…';
      try {
        const data = await call('/archive/vote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ candidate: cand.dataset.cand }),
        });
        selection = data.candidate;
        standings = data.leaderboard || standings;
        render();
        status.textContent = `Counted: ${cand.dataset.title}.`;
      } catch (err) {
        if (err.status === 409 && err.data?.candidate) {
          selection = err.data.candidate;
          await load();
          status.textContent = 'You already voted. One ballot per Shop account.';
          return;
        }
        button.disabled = false;
        button.textContent = 'Try Again';
        status.textContent = 'Your vote wasn’t counted. Try again in a moment.';
      }
    });

    box.closest('.os-win').addEventListener('os:open', () => loaded || load());

    // Back from Shop login: swap the one-time code for a session, then show the ballot.
    const code = location.hash.match(/(?:^#|&)auth=([^&]+)/);
    if (code) {
      history.replaceState(null, '', location.pathname + location.search);
      call('/auth/exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: decodeURIComponent(code[1]) }),
      })
        .then((data) => {
          session = data.token;
          try {
            localStorage.setItem(KEY, session);
          } catch (e) {}
          return load();
        })
        .then(() => open('ballot'))
        .catch((err) => {
          open('ballot');
          status.textContent = `Login not completed: ${err.message}`;
        });
    }
  }

  init(document.querySelector('[data-os]'));
  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector('[data-os]')));
})();
